import React, { useEffect } from "react";
import { DeviceAlarmManagerList } from "uis/MeetingRoomDeviceManagement";
import { TMember } from "types/user.types";
import {
  addDeviceAlarmChannel,
  addDeviceAlarmManager,
  deleteDeviceAlarmChannel,
  deleteDeviceAlarmManager,
  getDeviceAlarmChannelList,
  getDeviceAlarmManagerList,
} from "apis/meeting-room-device";
import { useToast } from "hook/useToast";
import { IReceiverMember } from "pages/meeting/complain-manager-setting/uis/table-view/person-table-view";
import { SettingContainer, Title, TopSection, Container } from "./SettingAlarmHost.styles";
import UI from "@mile-meeting/mile-ui";
import { AddMeetingManagerModal, AddMeetingSlackModal } from "uis/modal";
import DeviceAlarmChannelList from "uis/MeetingRoomDeviceManagement/DeviceAlarmChannelList/DeviceAlarmChannelList";
import { IAlarmChannel } from "apis/meeting-room-complain/alarm-channel";
import { getMessengers, TSlackChannel } from "apis/messangers";

type SettingAlarmHostProps = {
  isShow: boolean;
};

const SettingAlarmHost: React.FC<SettingAlarmHostProps> = ({ isShow }) => {
  const { showToast } = useToast();
  const [alarmReceiverList, setAlarmReceiverList] = React.useState<IReceiverMember[]>([]);
  const [isConnectedSlackApp, setIsConnectedSlackApp] = React.useState<boolean>(false);
  const [openAddManagerModal, setOpenAddManagerModal] = React.useState(false);
  const [openAddSlackModal, setOpenAddSlackModal] = React.useState(false);
  const [channelList, setChannelList] = React.useState<IAlarmChannel[]>([]);

  const onAddManager = async (members: TMember[]) => {
    const res = await addDeviceAlarmManager(members);
    if (res) {
      if (members.length === 1) showToast(`기기 관리 담당자 "${members[0].nickname}"이 추가되었습니다.`, "success");
      else
        showToast(`기기 관리 담당자 "${members[0].nickname}"외 ${members.length - 1}인이 추가되었습니다.`, "success");
    } else {
      showToast("담당자 추가에 실패했습니다. 재시도해 주세요.", "error");
    }
    setManagerList();
  };

  const onAddSlackChannel = async (channel: TSlackChannel) => {
    const res = await addDeviceAlarmChannel(channel);
    if (res) {
      showToast(`기기 관리 슬랙 채널 "${channel.slackChannelName}"이 추가되었습니다.`, "success");
      setChanelList();
    } else {
      showToast("슬랙 채널 추가에 실패했습니다. 재시도해 주세요.", "error");
    }
  };

  const setManagerList = async () => {
    const res = await getDeviceAlarmManagerList();
    setAlarmReceiverList(res);
  };

  const setChanelList = async () => {
    const res = await getDeviceAlarmChannelList();
    setChannelList(res);
  };

  const onDeleteManager = async (mediumId: string, name: string) => {
    const res = await deleteDeviceAlarmManager(mediumId);
    if (res) {
      showToast(`기기 관리 담당자에서 "${name}"이 삭제되었습니다.`, "success");
      setManagerList();
    } else {
      showToast("담당자 삭제에 실패했습니다. 재시도해 주세요.", "error");
    }
  };

  const onDeleteChannel = async (mediumId: string, name: string) => {
    const res = await deleteDeviceAlarmChannel(mediumId);
    if (res) {
      showToast(`기기 관리 슬랙 채널에서 "${name}"이 삭제되었습니다.`, "success");
      setChanelList();
    } else {
      showToast("채널 삭제에 실패했습니다. 재시도해 주세요", "error");
    }
  };

  const getIsConnectedSlackApp = async () => {
    const res = await getMessengers();
    if (!res.data) return setIsConnectedSlackApp(false);
    const isConnectedSlack = res.data.some((messenger) => messenger.messengerProvider === "SLACK");
    setIsConnectedSlackApp(isConnectedSlack);
  };

  useEffect(() => {
    setManagerList();
    setChanelList();
    getIsConnectedSlackApp();
  }, []);

  if (!isShow) return <></>;
  return (
    <Container>
      <SettingContainer>
        <TopSection>
          <Title>
            <UI.Typography type="headline01" color="var(--alias-color-label-normal)">
              기기 관리 담당자
            </UI.Typography>
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-alternative)">
              회의실 기기에 이상이 감지될 경우, 기기 관리 담당자에게 알림이 전송됩니다.
            </UI.Typography>
          </Title>
          <UI.Button
            title="담당자 추가하기"
            primary={false}
            prefixIcon="addPerson"
            showPrefixIcon
            onClick={() => {
              setOpenAddManagerModal(true);
            }}
          />
        </TopSection>
        <DeviceAlarmManagerList managerList={alarmReceiverList} onHandleDeleteManager={onDeleteManager} />
      </SettingContainer>
      <AddMeetingManagerModal
        existedMembers={alarmReceiverList.map((member) => member.host)}
        modalIsOpen={openAddManagerModal}
        closeModal={() => setOpenAddManagerModal(false)}
        title={"기기 관리 담당자 추가"}
        onHandleAddManager={async (selectedMembers: TMember[]) => {
          await onAddManager(selectedMembers);
          setOpenAddManagerModal(false);
        }}
      />
      <SettingContainer>
        <TopSection>
          <Title>
            <UI.Typography type="headline01" color="var(--alias-color-label-normal)">
              기기 관리 슬랙 채널
            </UI.Typography>
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-alternative)">
              회의실 기기에 이상이 감지될 경우, 슬랙 채널로 알림이 전송됩니다.
            </UI.Typography>
          </Title>
          <UI.Button
            disabled={!isConnectedSlackApp}
            title="슬랙 채널 추가하기"
            primary={false}
            prefixIcon="view"
            showPrefixIcon
            onClick={() => {
              if (!isConnectedSlackApp) {
                showToast("슬랙 미연동 상태입니다. 슬랙 연동 후 채널을 추가해 주세요.", "warning");
                return;
              }
              setOpenAddSlackModal(true);
            }}
          />
        </TopSection>
        <DeviceAlarmChannelList channelList={channelList} onHandleDeleteChannel={onDeleteChannel} />
      </SettingContainer>
      <AddMeetingSlackModal
        modalIsOpen={openAddSlackModal}
        closeModal={() => setOpenAddSlackModal(false)}
        onHandleGuide={() => {
          window.open(
            "https://mile-corporation.notion.site/1c4a222a9c1b80efb150ffdb75144120#232a222a9c1b80be9e1cfbdc374526b3"
          );
        }}
        title={"기기 관리 슬랙 채널 추가"}
        onHandleAddChannel={async (channel) => {
          if (!channel) return;
          await onAddSlackChannel(channel);
          setOpenAddSlackModal(false);
        }}
      />
    </Container>
  );
};

export default React.memo(SettingAlarmHost);
